import { useAutocompleteService } from '@ubilabs/google-maps-react-hooks';
import { useRef, useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { GeoLocator } from '../components/GeoLocator';
import { Input } from '../components/Input';
import { useLocatorStore } from '../store/useLocatorStore';

export interface IAutoCompleteSuggestion {
  id: string;
  label: string;
}

const maxNumberOfSuggestions = 7;

type Props = {
  className?: string;
  label?: string;
};

export function AutoComplete({ className = '', label = '' }: Props) {
  const { t } = useTranslation();

  const searchInput = useLocatorStore((state) => state.searchInput);
  const setSearchInput = useLocatorStore((state) => state.setSearchInput);
  const mapViewBounds = useLocatorStore((state) => state.mapViewBounds);
  const setShouldGeoLocate = useLocatorStore((state) => state.setShouldGeoLocate);

  const setCurrentLocation = useLocatorStore((state) => state.setCurrentLocation);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const [suggestions, setSuggestions] = useState<Array<IAutoCompleteSuggestion>>([]);

  const [highlightedSuggestion, setHighlightedSuggestion] = useState<number>(0);

  const [suggestionsAreVisible, setSuggestionsAreVisible] = useState<boolean>(false);

  const autocompleteService = useAutocompleteService();

  // Update the user input value
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    // Show dropdown with a little delay
    timeout.current = setTimeout(() => {
      setSuggestionsAreVisible(true);
    }, 300);
  };

  function focusInput() {
    inputRef.current?.focus();
  }

  // Handle suggestion selection
  const selectSuggestion = (suggestion: IAutoCompleteSuggestion) => {
    inputRef.current?.focus();
    setSearchInput(suggestion.label);
    setCurrentLocation(undefined);
    setShouldGeoLocate(true);

    // Close dropdown
    setSuggestionsAreVisible(false);
    // @ts-expect-error
    if (window.dataLayer) {
      // @ts-expect-error
      window.dataLayer.push({
        'search_text': suggestion.label,
        'event': 'Location Search',
      });
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Update suggestions and get autocomplete place suggestions
  useEffect(() => {
    if (searchInput.length === 0) {
      setSuggestionsAreVisible(false);
    }
    if (searchInput.length! >= 2) {
      autocompleteService?.getPlacePredictions(
        {
          input: searchInput!,
        },
        (
          predictions: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            return;
          }

          const autocompleteSuggestions = predictions
            .slice(0, maxNumberOfSuggestions)
            .filter((prediction) => {
              // Filter only for cities, regions, or postcodes
              return (
                prediction.types.includes('locality') ||
                prediction.types.includes('political') ||
                prediction.types.includes('postal_code') ||
                prediction.types.includes('street_address') ||
                prediction.types.includes('route') ||
                prediction.types.includes('premise')
              );
            })
            .map((prediction) => ({
              id: prediction.place_id,
              label: prediction.description,
            }));

          // Update suggestions for dropdown suggestions list
          setSuggestions(autocompleteSuggestions);
        }
      );
    } else {
      setSuggestions([]);
    }
  }, [searchInput]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38) {
      if (highlightedSuggestion > 0) {
        setHighlightedSuggestion(highlightedSuggestion - 1);
      }
    } else if (e.keyCode === 40) {
      if (highlightedSuggestion < suggestions.length - 1) {
        setHighlightedSuggestion(highlightedSuggestion + 1);
      }
    }
    // handle enter key
    else if (e.keyCode === 13) {
      if (mapViewBounds && location.pathname === '/') {
        navigate('/map');

        return;
      }
      selectSuggestion(suggestions[highlightedSuggestion]);
    }
  }

  useEffect(() => {
    setHighlightedSuggestion(0);
  }, [suggestionsAreVisible]);

  return (
    <div className={`relative ${className}`}>
      <Input
        name="search"
        type="text"
        label={label}
        placeholder={t("e.g. 'London' or 'NW3'")}
        inputClassName="h-[52px] pe-[52px] location-search-text-field"
        appendedEl={<GeoLocator focusInput={focusInput} />}
        ref={inputRef}
        value={searchInput}
        onChange={handleInputChange}
        aria-autocomplete="list"
        aria-controls="search-suggestions"
        aria-expanded={suggestionsAreVisible}
        onKeyDown={handleKeyDown}
      />

      {suggestionsAreVisible && (
        <ul
          id="search-suggestions"
          role="listbox"
          className="absolute left-0  z-10 w-full bg-secondary slick-dots"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              onClick={() => selectSuggestion(suggestion)}
              id={suggestion.id}
              role="option"
              className={`cursor-pointer px-[16px] py-[12px]  ${
                highlightedSuggestion === index ? 'bg-primary bg-opacity-10' : ''
              }`}
            >
              <span>{suggestion.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
